import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';

import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { UpdateBooleanServerSettingNames } from '../../../../ServerApi/requests/serverSettings/UpdateBooleanServerSettingRequest';
import { ServerSettingsInstance } from '../../../../services/ServerSettings';
import { disabledText, SettingsStyles } from '../../../../styles/CommonStyles';
import { useSelector } from '../../../utils';
import { ServerSettingsProps } from '../../server-settings/ReactServerSettingsSubFormik';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';

export const EMBEDDED_CREDENTIALS_SECTION_TEST_ID = 'embedded-credentials-setting-section';
export const EMBEDDED_CREDENTIALS_SUB_SECTION_TEST_ID = 'embedded-credentials-setting-sub-section';
export const EMBEDDED_CREDENTIALS_CHECKBOX_TEST_ID = 'embedded-credentials-enabled';
export const SCHEDULE_RUNS_CHECKBOX_TEST_ID = 'scheduling-enabled-checkbox';

export type EmbeddedCredentialsSectionProps =
  ServerSettingsProps<
    'embeddedCredentialsEnabled' |
    'schedulingEnabled'>;

export function EmbeddedCredentialsSection(props: EmbeddedCredentialsSectionProps): JSX.Element {
  const formik = useFormikContext<EmbeddedCredentialsSectionProps>();
  const isFlowEnabled = useSelector(SessionServerSelectors.isFlowOnServerEnabled);

  return (
    <SettingsSection
      title={Messages.embeddedCredentials()}
      description={isFlowEnabled ?
        Messages.embeddedCredentialsDetailsWithFlows() :
        Messages.embeddedCredentialsDetails()}
      testId={EMBEDDED_CREDENTIALS_SECTION_TEST_ID}>
      {renderEmbedCredentialsCheckbox(isFlowEnabled)}
      {renderScheduleFlowRunsCheckbox(formik, isFlowEnabled)}
    </SettingsSection>
  );
}

function renderEmbedCredentialsCheckbox(isFlowEnabled: boolean): JSX.Element {
  return <div className={SettingsStyles.checkboxGroup}>
    <FormikCheckbox
      name='settings.embeddedCredentialsEnabled'
      label={isFlowEnabled ?
        Messages.embeddedCredentialsLabelWithFlows() :
        Messages.embeddedCredentialsLabel()}
      testId={EMBEDDED_CREDENTIALS_CHECKBOX_TEST_ID}
    />
  </div>;
}

function renderScheduleFlowRunsCheckbox(
  formik: FormikContextType<EmbeddedCredentialsSectionProps>,
  isFlowEnabled: boolean
): JSX.Element {

  const isEmbeddedCredentialsEnabled = formik.values.settings.embeddedCredentialsEnabled;
  const schedulingDetailsText = isFlowEnabled ?
    Messages.schedulingDetailsWithFlows() :
    Messages.schedulingDetails();
  const subGroupDescriptionClassName = `${SettingsStyles.settingsSubGroup}
  ${isEmbeddedCredentialsEnabled ? '' : disabledText}`;

  return <div className={subGroupDescriptionClassName}>
    <div
      className={SettingsStyles.settingsGroupDescription}
      data-tb-test-id={EMBEDDED_CREDENTIALS_SUB_SECTION_TEST_ID}>
      {schedulingDetailsText}
    </div>
    <div className={SettingsStyles.checkboxGroup}>
      <FormikCheckbox
        name='settings.schedulingEnabled'
        label={isFlowEnabled ?
          Messages.schedulingControlLabelWithFlows() :
          Messages.schedulingControlLabel()}
        testId={SCHEDULE_RUNS_CHECKBOX_TEST_ID}
        disabled={!isEmbeddedCredentialsEnabled}
      />
    </div>
  </div>;
}

/* for registering the component in settings registry */
export const EmbeddedCredentialsSectionSpecs = componentRegistryEntry({
  name: 'EmbeddedCredentialsSection',
  component: EmbeddedCredentialsSection,
  onSubmit: async (settings) => {
    const serverSettingsService = ServerSettingsInstance;
    const booleanNames = UpdateBooleanServerSettingNames;

    await serverSettingsService.updateBoolean(booleanNames.embeddedCredentialsEnabled, settings.embeddedCredentialsEnabled)
      .then(() => {
        // Since scheduling depends on embeddedCredentials being enabled, make sure embeddedCredentials
        // saves first and only save scheduling if embeddedCredentials is enabled
        if (settings.embeddedCredentialsEnabled) {
          return serverSettingsService.updateBoolean(booleanNames.schedulingEnabled, settings.schedulingEnabled);
        }
      });
  }
});
